/**
 * Blackjack — CraftPix kit, chips, real play GBUX vs BUDBai house bank.
 * Engine: shared/blackjack-engine.ts · API: worker/src/blackjack.ts
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { useWallet, useWalletState } from "@/contexts/wallet-context";
import { formatPlayGbux } from "@shared/gbux-math";
import {
  craftpixCardUrl,
  handTotal,
  type BjPublicState,
  type Card,
  BJ_MIN_BET,
  BJ_MAX_INITIAL_BET,
  BJ_CHIP_DENOMS,
  BJ_MATH,
} from "@shared/blackjack-engine";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  Coins,
  Loader2,
  Wallet,
  Spade,
  Info,
} from "lucide-react";
import { casino } from "@/lib/casino-sfx";
import { useDappSign } from "@/contexts/dapp-sign-context";
import { playSessionHeaders } from "@/lib/play-session";
import { warmPlaySession } from "@/lib/play-ledger-session";
import { useDisplay } from "@/hooks/use-display";
import {
  RacalvinDealerEmbed,
  relayChatToViduPersona,
} from "@/components/vidu-persona-embed";
import {
  FundPlayDialog,
  PlaySessionStatusLine,
} from "@/components/play-ledger-dialogs";
import "@/styles/gbux-games.css";

const BJ_UI = {
  hit: "/blackjack/interface/button_hit.png",
  stand: "/blackjack/interface/button_stand.png",
  double: "/blackjack/interface/button_double.png",
  tableWeb: "/media/blackjack/table-web.jpg",
  tablePhone: "/media/blackjack/table-phone.jpg",
  tableKit: "/blackjack/table/table_1.png",
} as const;

type State = BjPublicState & {
  playGbux?: number;
  houseBankGbux?: number;
  houseWallet?: string;
  houseName?: string;
  message?: string;
  credited?: number;
  math?: typeof BJ_MATH;
};

const CHIP_SRC: Record<number, string> = {
  1: "/chips/1.png",
  5: "/chips/4.png", // nearest visual
  10: "/chips/10.png",
  25: "/chips/25.png",
  50: "/chips/50.png",
  100: "/chips/100.png",
  500: "/chips/500.png",
  1000: "/chips/1000.png",
};

function ChipImg({
  denom,
  size = 44,
  pulse,
}: {
  denom: number;
  size?: number;
  pulse?: boolean;
}) {
  const src = CHIP_SRC[denom] || "/chips/25.png";
  return (
    <motion.img
      src={src}
      alt={`${denom} GBUX`}
      draggable={false}
      initial={pulse ? { scale: 0.6, y: 12, opacity: 0 } : false}
      animate={{ scale: 1, y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 420, damping: 18 }}
      style={{
        width: size,
        height: size,
        objectFit: "contain",
        filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.55))",
      }}
      onError={(e) => {
        (e.target as HTMLImageElement).src = "/chips/25.png";
      }}
    />
  );
}

/** Break amount into chip stack denoms (greedy). */
function stackChips(amount: number): number[] {
  let left = Math.max(0, Math.floor(amount));
  const out: number[] = [];
  const denoms = [...BJ_CHIP_DENOMS].sort((a, b) => b - a);
  for (const d of denoms) {
    while (left >= d && out.length < 12) {
      out.push(d);
      left -= d;
    }
  }
  return out;
}

function newIdemKey(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `k-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

class BjApiError extends Error {
  needPlaySession?: boolean;
  required?: number;
  available?: number;
  constructor(
    message: string,
    extra?: { needPlaySession?: boolean; required?: number; available?: number },
  ) {
    super(message);
    this.name = "BjApiError";
    this.needPlaySession = extra?.needPlaySession;
    this.required = extra?.required;
    this.available = extra?.available;
  }
}

async function api(
  path: string,
  opts?: {
    method?: string;
    body?: unknown;
    wallet?: string;
    idempotencyKey?: string;
    /** Attach X-Play-Session for money moves */
    playSession?: boolean;
  },
) {
  const base: HeadersInit = { "Content-Type": "application/json" };
  const headers = opts?.playSession
    ? playSessionHeaders(opts.wallet, base)
    : new Headers(base);
  if (opts?.wallet) headers.set("X-Wallet-Address", opts.wallet);
  if (opts?.idempotencyKey) {
    headers.set("Idempotency-Key", opts.idempotencyKey);
  }
  const r = await fetch(path, {
    method: opts?.method || "GET",
    headers,
    body: opts?.body ? JSON.stringify(opts.body) : undefined,
  });
  const j = (await r.json()) as State & {
    error?: string;
    tip?: string;
    needPlaySession?: boolean;
    required?: number;
    available?: number;
  };
  if (!r.ok) {
    throw new BjApiError(j.error || j.tip || r.statusText, {
      needPlaySession: Boolean(j.needPlaySession),
      required: j.required,
      available: j.available,
    });
  }
  return j as State;
}

function CardView({ card, small }: { card: Card; small?: boolean }) {
  const src = craftpixCardUrl(card);
  const h = small ? 64 : 88;
  return (
    <img
      src={src}
      alt={`${card.rank} ${card.suit}`}
      style={{
        height: h,
        width: "auto",
        borderRadius: 6,
        boxShadow: "0 4px 12px rgba(0,0,0,0.45)",
        objectFit: "contain",
      }}
      onError={(e) => {
        (e.target as HTMLImageElement).src = "/blackjack/cards/1/S.png";
      }}
    />
  );
}

export default function BlackjackPage() {
  const { publicKey, playGbux, connected, refreshBalances } = useWallet();
  const { openWalletPanel } = useWalletState();
  const { ensurePlaySession } = useDappSign();
  const display = useDisplay();
  const phone = display.useVerticalTable || display.isPhone;
  const [, setLocation] = useLocation();
  const wallet =
    typeof publicKey === "string"
      ? publicKey
      : publicKey && typeof (publicKey as { toString?: () => string }).toString ===
          "function"
        ? (publicKey as { toString: () => string }).toString()
        : "";

  const [state, setState] = useState<State | null>(null);
  const [bets, setBets] = useState([25, 0, 0]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [activeHand, setActiveHand] = useState(0);
  const [chipPulse, setChipPulse] = useState(0);
  const [showMath, setShowMath] = useState(false);
  const [fundOpen, setFundOpen] = useState(false);

  const load = useCallback(async () => {
    if (!wallet) return;
    try {
      // Warm 24h play session (no popup if token still valid)
      await warmPlaySession(wallet);
      const s = await api(
        `/api/blackjack/state?wallet=${encodeURIComponent(wallet)}`,
        { wallet },
      );
      setState(s);
      if (typeof s.playGbux === "number" && s.playGbux <= 0) {
        setFundOpen(true);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [wallet]);

  useEffect(() => {
    void load();
    casino.unlock();
  }, [load]);

  const run = async (
    fn: () => Promise<State>,
    sfx?: "deal" | "hit" | "stand" | "double" | "bet",
  ) => {
    setBusy(true);
    setErr(null);
    if (sfx === "bet") casino.bet();
    try {
      const s = await fn();
      setState(s);
      void refreshBalances();
      // Outcome SFX after server state
      if (sfx === "deal") {
        casino.shuffle();
        window.setTimeout(() => casino.deal(4), 150);
      } else if (sfx === "hit") casino.hit();
      else if (sfx === "stand") casino.stand();
      else if (sfx === "double") {
        casino.bet();
        casino.hit();
      }
      const results = s.hands;
      if (results?.length) {
        for (const h of results) {
          const cards = h.cards || [];
          if (cards.length >= 2) {
            const t = handTotal(cards);
            if (t.total === 21) {
              if (cards.length === 2) casino.blackjack();
              else casino.win();
            } else if (t.total > 21) casino.bust();
          }
          if (h.status === "blackjack") casino.blackjack();
          if (h.status === "bust") casino.bust();
        }
      }
      if (s.credited && s.credited > 0) casino.win();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      const apiErr = e instanceof BjApiError ? e : null;
      if (
        apiErr?.needPlaySession ||
        /play session|sign|authorize/i.test(msg)
      ) {
        setErr(
          "Play session required — sign once for tables, slots & blackjack (24h).",
        );
        try {
          await ensurePlaySession();
          setErr("Session ready — try Deal again.");
        } catch {
          /* user cancelled sign */
        }
      } else if (
        /insufficient|not enough|fund/i.test(msg) ||
        (apiErr?.available != null &&
          apiErr?.required != null &&
          apiErr.available < apiErr.required)
      ) {
        setFundOpen(true);
        setErr(
          apiErr?.required != null
            ? `Need ${apiErr.required} play GBUX (have ${apiErr.available ?? 0}). Fund play wallet.`
            : msg,
        );
      } else {
        setErr(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const bal = state?.playGbux ?? playGbux ?? 0;
  const houseBank = state?.houseBankGbux ?? null;
  const phase = state?.phase || "idle";
  const playing = phase === "player";
  const [dealerTalk, setDealerTalk] = useState("");
  const totalBet = useMemo(() => bets.reduce((a, b) => a + b, 0), [bets]);

  const deal = () => {
    const key = newIdemKey();
    const safeBets = bets.map((b) =>
      b <= 0 ? 0 : Math.min(BJ_MAX_INITIAL_BET, Math.max(0, Math.floor(b))),
    );
    const wager = safeBets.reduce((a, b) => a + b, 0);
    if (wager > 0 && bal < wager) {
      setFundOpen(true);
      return;
    }
    return run(async () => {
      // Uses stored 24h session when valid — no re-sign across pages
      await ensurePlaySession();
      return api("/api/blackjack/deal", {
        method: "POST",
        wallet,
        idempotencyKey: key,
        playSession: true,
        body: { wallet, bets: safeBets, idempotencyKey: key },
      });
    }, "deal");
  };

  const hit = (i: number) =>
    run(
      () =>
        api("/api/blackjack/hit", {
          method: "POST",
          wallet,
          body: { wallet, handIndex: i },
        }),
      "hit",
    );

  const standH = (i: number) =>
    run(
      () =>
        api("/api/blackjack/stand", {
          method: "POST",
          wallet,
          body: { wallet, handIndex: i },
        }),
      "stand",
    );

  const dbl = (i: number) => {
    const handBet = state?.hands?.[i]?.bet ?? 0;
    if (handBet > 0 && bal < handBet) {
      setFundOpen(true);
      setErr(
        `Double needs ${handBet} more play GBUX (balance ${formatPlayGbux(bal)}).`,
      );
      return;
    }
    return run(async () => {
      await ensurePlaySession();
      return api("/api/blackjack/double", {
        method: "POST",
        wallet,
        playSession: true,
        body: { wallet, handIndex: i },
      });
    }, "double");
  };

  const addChip = (handIndex: number, denom: number) => {
    setBets((prev) => {
      const next = [...prev];
      next[handIndex] = Math.min(
        BJ_MAX_INITIAL_BET,
        Math.max(0, Math.floor(next[handIndex] || 0) + denom),
      );
      return next;
    });
    setActiveHand(handIndex);
    setChipPulse((n) => n + 1);
    try {
      casino.bet();
    } catch {
      /* optional sfx */
    }
  };

  const clearHandBet = (handIndex: number) => {
    setBets((prev) => {
      const next = [...prev];
      next[handIndex] = 0;
      return next;
    });
  };

  // Notify Racalvin voice of round outcomes (text_msg → Live persona)
  useEffect(() => {
    if (!state?.message || phase === "player") return;
    const m = state.message;
    if (
      /win|bust|blackjack|push|dealer|stand|dealt/i.test(m) &&
      m !== "Place bets on 1–3 hands"
    ) {
      relayChatToViduPersona(
        `Table update: ${m}. React short as dealer.`,
        "racalvin",
      );
    }
  }, [state?.message, phase]);

  return (
    <div className="bj-page-prod bj-page-mobile">
      <FundPlayDialog
        open={fundOpen}
        onClose={() => setFundOpen(false)}
        playGbux={bal}
        game="blackjack"
        onFund={() => openWalletPanel("deposit")}
      />
      <header className="bj-prod-header border-b border-emerald-900/40 bg-black/60 backdrop-blur-md sticky top-0 z-20">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-2 px-3 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <Button
              variant="outline"
              size="sm"
              className="border-zinc-700 shrink-0"
              onClick={() => setLocation("/gbuxgames")}
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Games
            </Button>
            <div className="min-w-0">
              <h1 className="font-bold text-base flex items-center gap-1.5 truncate tracking-tight">
                <Spade className="h-4 w-4 text-emerald-400 shrink-0" />
                Blackjack · vs BUDBai
              </h1>
              <p className="text-[10px] text-zinc-500 truncate leading-snug">
                Real play GBUX · 3-deck shoe · house pays wins
              </p>
              <PlaySessionStatusLine className="text-[10px] text-emerald-400/85" />
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end">
            <button
              type="button"
              className="bj-balance-pill rounded-lg border border-emerald-500/35 bg-emerald-950/50 px-2.5 py-1.5 text-left min-h-[40px]"
              onClick={() => {
                if (!connected) openWalletPanel("connect");
                else if (bal <= 0) setFundOpen(true);
                else openWalletPanel("deposit");
              }}
              title="Play GBUX ledger balance"
            >
              <div className="text-[9px] uppercase tracking-wider text-zinc-500 leading-none">
                Play GBUX
              </div>
              <strong className="text-emerald-300 text-sm tabular-nums leading-tight">
                {connected ? formatPlayGbux(bal) : "—"}
              </strong>
            </button>
            {houseBank != null && (
              <div className="hidden sm:block rounded-lg border border-amber-500/25 bg-amber-950/30 px-2 py-1.5 text-[11px] min-h-[40px]">
                <div className="text-[9px] uppercase tracking-wider text-zinc-500 leading-none">
                  House bank
                </div>
                <strong className="text-amber-200 tabular-nums">
                  {formatPlayGbux(houseBank)}
                </strong>
              </div>
            )}
            <Button
              size="sm"
              variant="outline"
              className="border-zinc-700 min-h-[40px]"
              onClick={() => setShowMath((v) => !v)}
              aria-label="Math rules"
            >
              <Info className="h-4 w-4" />
            </Button>
            {!connected ? (
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-500 min-h-[40px]"
                onClick={() => openWalletPanel("connect")}
              >
                <Wallet className="h-4 w-4 mr-1" /> Connect
              </Button>
            ) : (
              <Button
                size="sm"
                className="bg-amber-600 hover:bg-amber-500 text-black font-semibold min-h-[40px]"
                onClick={() => openWalletPanel("deposit")}
              >
                <Coins className="h-4 w-4 mr-1" /> Fund
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="bj-stage">
        {!connected && (
          <div className="w-full rounded-xl border border-amber-500/30 bg-amber-950/20 p-4 mb-3 text-sm">
            Connect a wallet to play. Bets debit{" "}
            <strong className="text-emerald-300">play GBUX</strong>.
            <Button
              className="mt-3 w-full sm:w-auto"
              onClick={() => openWalletPanel("connect")}
            >
              Connect wallet
            </Button>
          </div>
        )}

        {err && (
          <div className="mb-3 w-full rounded-lg border border-red-500/40 bg-red-950/30 px-3 py-2 text-sm text-red-200 flex flex-wrap items-center justify-between gap-2">
            <span className="min-w-0 leading-snug">{err}</span>
            {/fund|insufficient|play GBUX/i.test(err) && (
              <Button
                size="sm"
                className="bg-amber-600 text-black shrink-0"
                onClick={() => openWalletPanel("deposit")}
              >
                Fund play
              </Button>
            )}
          </div>
        )}

        {connected &&
          typeof state?.credited === "number" &&
          state.credited > 0 &&
          phase === "settled" && (
            <div className="mb-3 w-full rounded-lg border border-emerald-500/35 bg-emerald-950/35 px-3 py-2 text-sm text-emerald-100 flex items-center justify-between gap-2">
              <span>
                Credited to play ledger:{" "}
                <strong className="text-emerald-300">
                  +{formatPlayGbux(state.credited)} GBUX
                </strong>
              </span>
              <ChipImg denom={25} size={28} />
            </div>
          )}

        {/* Full-height CraftPix table container */}
        <div
          className={`bj-table ${phone ? "bj-table--phone" : ""}`}
        >
          <img
            className="bj-table__img"
            src={phone ? BJ_UI.tablePhone : BJ_UI.tableWeb}
            alt=""
            onError={(e) => {
              (e.target as HTMLImageElement).src = BJ_UI.tableKit;
            }}
          />
          <div className="bj-table__shade" />
          <div className="bj-table__play">
            {/* Dealer + Racalvin voice head */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <div
                className="shrink-0 w-[120px] sm:w-[140px]"
                title="Racalvin · Vidu dealer (voice + face)"
              >
                <RacalvinDealerEmbed
                  compact
                  position="relative"
                  autoStart={connected}
                />
              </div>
              <div className="text-center flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest text-emerald-200/70 mb-2">
                  Racalvin · Dealer
                  {state?.dealer?.cards?.length
                    ? ` · ${
                        state.dealer.holeHidden
                          ? "?"
                          : handTotal(
                              state.dealer.cards.filter((c) => c.rank !== "?"),
                            ).total
                      }`
                    : ""}
                </div>
                <div className="flex justify-center gap-1 flex-wrap min-h-[90px]">
                  {(state?.dealer?.cards || []).map((c, i) => (
                    <CardView key={i} card={c} />
                  ))}
                </div>
              </div>
            </div>
            {connected && (
              <div className="flex gap-1.5 mt-2 max-w-md mx-auto w-full px-1">
                <Input
                  placeholder="Talk to Racalvin…"
                  value={dealerTalk}
                  onChange={(e) => setDealerTalk(e.target.value)}
                  className="bg-black/40 border-emerald-900/50 h-9 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && dealerTalk.trim()) {
                      relayChatToViduPersona(dealerTalk.trim(), "racalvin");
                      setDealerTalk("");
                    }
                  }}
                />
                <Button
                  size="sm"
                  className="bg-emerald-700 hover:bg-emerald-600 shrink-0"
                  onClick={() => {
                    if (!dealerTalk.trim()) return;
                    relayChatToViduPersona(dealerTalk.trim(), "racalvin");
                    setDealerTalk("");
                  }}
                >
                  Say
                </Button>
              </div>
            )}

            <div className="text-center text-sm text-amber-100/80 font-medium">
              {state?.message || "Place bets on 1–3 hands"}
              {typeof state?.shoeRemaining === "number" && (
                <span className="text-zinc-500 text-xs ml-2">
                  · shoe {state.shoeRemaining}
                </span>
              )}
            </div>

            {/* 3 hands */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-auto">
              {[0, 1, 2].map((i) => {
                const h = state?.hands?.[i];
                const cards = h?.cards || [];
                const tot =
                  cards.length > 0 ? handTotal(cards).total : null;
                const isActive =
                  playing &&
                  (h?.status === "active" || h?.status === "doubled");
                return (
                  <div
                    key={i}
                    role="button"
                    tabIndex={0}
                    data-active={activeHand === i ? "1" : "0"}
                    onClick={() => setActiveHand(i)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") setActiveHand(i);
                    }}
                    className="bj-hand-slot cursor-pointer"
                  >
                    <div className="flex justify-between text-[11px] text-zinc-400 mb-2">
                      <span>Hand {i + 1}</span>
                      <span className="text-amber-300 font-bold">
                        {h?.bet
                          ? `${h.bet}`
                          : bets[i]
                            ? `${bets[i]}`
                            : "—"}
                      </span>
                    </div>
                    {/* Chip stack on bet */}
                    <div className="flex justify-center min-h-[28px] mb-1">
                      <AnimatePresence mode="popLayout">
                        {stackChips(
                          phase === "idle" || phase === "settled"
                            ? bets[i] || 0
                            : h?.bet || 0,
                        )
                          .slice(0, 6)
                          .map((d, di) => (
                            <motion.div
                              key={`${chipPulse}-${i}-${di}-${d}`}
                              className="-ml-2 first:ml-0"
                              style={{ zIndex: di }}
                            >
                              <ChipImg denom={d} size={28} pulse />
                            </motion.div>
                          ))}
                      </AnimatePresence>
                    </div>
                    <div className="flex gap-0.5 min-h-[64px] flex-wrap justify-center">
                      {cards.map((c, ci) => (
                        <CardView key={ci} card={c} small />
                      ))}
                    </div>
                    <div className="mt-1.5 text-center text-sm font-bold">
                      {tot != null ? (
                        <span
                          className={
                            tot > 21
                              ? "text-red-400"
                              : h?.status === "blackjack"
                                ? "text-amber-300"
                                : "text-white"
                          }
                        >
                          {tot}
                          {h?.status === "blackjack" && " BJ"}
                          {h?.status === "bust" && " BUST"}
                        </span>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </div>
                    {h?.result != null && phase === "settled" && (
                      <div
                        className={`text-center text-xs font-bold mt-1 ${
                          h.result > 0
                            ? "text-emerald-400"
                            : h.result < 0
                              ? "text-red-400"
                              : "text-zinc-400"
                        }`}
                      >
                        {h.result > 0 ? "+" : ""}
                        {h.result}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* CraftPix action strip (active hands) */}
            {playing && (
              <div className="bj-actions">
                {[0, 1, 2]
                  .filter((i) => {
                    const h = state?.hands?.[i];
                    return (
                      h &&
                      (h.status === "active" || h.status === "doubled")
                    );
                  })
                  .slice(0, 1)
                  .map((i) => {
                    const cards = state?.hands?.[i]?.cards || [];
                    return (
                      <div key={i} className="bj-actions flex w-full justify-center gap-3">
                        <button
                          type="button"
                          className="bj-act-btn"
                          disabled={busy}
                          onClick={() => void hit(i)}
                          aria-label="Hit"
                        >
                          <img
                            className="bj-act-img"
                            src={BJ_UI.hit}
                            alt="Hit"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display =
                                "none";
                            }}
                          />
                        </button>
                        <button
                          type="button"
                          className="bj-act-btn"
                          disabled={busy}
                          onClick={() => void standH(i)}
                          aria-label="Stand"
                        >
                          <img
                            className="bj-act-img"
                            src={BJ_UI.stand}
                            alt="Stand"
                          />
                        </button>
                        <button
                          type="button"
                          className="bj-act-btn"
                          disabled={busy || cards.length !== 2}
                          onClick={() => void dbl(i)}
                          aria-label="Double"
                        >
                          <img
                            className="bj-act-img"
                            src={BJ_UI.double}
                            alt="Double"
                          />
                        </button>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>

        {(phase === "idle" || phase === "settled") && connected && (
          <div className="bj-chip-tray mt-3 w-full rounded-xl border border-zinc-800 bg-zinc-950/95 p-3 space-y-3 shadow-lg shadow-black/40">
            <div className="text-xs font-semibold text-zinc-300 flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <Coins className="h-3.5 w-3.5 text-amber-400" />
                Chip tray · hand {activeHand + 1}
              </span>
              <span className="text-[10px] text-zinc-500 font-normal tabular-nums">
                Play {formatPlayGbux(bal)} · min {BJ_MIN_BET} · max{" "}
                {BJ_MAX_INITIAL_BET}/hand
              </span>
            </div>
            {bal <= 0 && (
              <button
                type="button"
                className="w-full rounded-lg border border-amber-500/40 bg-amber-950/40 px-3 py-2 text-left text-xs text-amber-100"
                onClick={() => setFundOpen(true)}
              >
                Play balance is 0 — tap to fund real GBUX (SPL or buy @ $0.001)
                before dealing.
              </button>
            )}

            {/* Visual chip tray */}
            <div className="flex flex-wrap justify-center gap-2 py-1">
              {BJ_CHIP_DENOMS.map((d) => (
                <button
                  key={d}
                  type="button"
                  className="bj-chip-btn flex flex-col items-center gap-0.5 rounded-xl border border-zinc-700/80 bg-black/40 px-1.5 py-1 transition hover:border-amber-500/50 hover:scale-105 active:scale-95 disabled:opacity-40"
                  disabled={busy || bal < d}
                  onClick={() => addChip(activeHand, d)}
                  title={`Add ${d} to hand ${activeHand + 1}`}
                >
                  <ChipImg denom={d} size={48} />
                  <span className="text-[10px] font-bold text-amber-200/90">
                    {d}
                  </span>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className={`rounded-lg border p-2 ${
                    activeHand === i
                      ? "border-emerald-500/50 bg-emerald-950/30"
                      : "border-zinc-800 bg-zinc-900/50"
                  }`}
                >
                  <button
                    type="button"
                    className="w-full text-left"
                    onClick={() => setActiveHand(i)}
                  >
                    <div className="text-[10px] text-zinc-500">Hand {i + 1}</div>
                    <div className="text-lg font-black tabular-nums text-amber-200">
                      {bets[i] || 0}
                    </div>
                  </button>
                  <div className="flex gap-1 mt-1">
                    <button
                      type="button"
                      className="flex-1 text-[10px] py-1 rounded bg-zinc-800 border border-zinc-700"
                      onClick={() => clearHandBet(i)}
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      className="flex-1 text-[10px] py-1 rounded bg-zinc-800 border border-zinc-700"
                      onClick={() => {
                        setBets((p) => {
                          const n = [...p];
                          n[i] = Math.min(
                            BJ_MAX_INITIAL_BET,
                            Math.floor(Number(bal) || 0),
                          );
                          return n;
                        });
                        setActiveHand(i);
                      }}
                    >
                      Max
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-500 h-12 text-base font-bold"
              disabled={busy || !wallet || totalBet < BJ_MIN_BET || totalBet > bal}
              onClick={() => void deal()}
            >
              {busy ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : (
                <span className="mr-2 inline-flex">
                  <ChipImg denom={25} size={22} />
                </span>
              )}
              Deal · {totalBet} GBUX
            </Button>
            {totalBet > bal && (
              <p className="text-center text-[11px] text-red-300">
                Bet exceeds play balance — fund play GBUX first.
              </p>
            )}
          </div>
        )}

        {showMath && (
          <div className="mt-3 w-full rounded-xl border border-emerald-800/40 bg-black/70 p-3 text-[11px] text-zinc-400 space-y-1">
            <div className="font-bold text-emerald-200 text-xs">
              Blackjack math · real GBUX
            </div>
            <p>
              {BJ_MATH.decks} decks · natural {BJ_MATH.naturalPayout} · even
              money {BJ_MATH.evenMoney} · S17 · double first two cards
            </p>
            <p>
              Bets debit your play ledger and credit{" "}
              <strong className="text-amber-200">BUDBai house bank</strong>.
              Wins pay from house bank back to play (1:1 or 3:2 natural).
            </p>
            <p className="font-mono text-[10px] text-zinc-600">
              House {state?.houseWallet?.slice(0, 12) || "agent"}… · bank{" "}
              {houseBank != null ? formatPlayGbux(houseBank) : "—"} · $
              {BJ_MATH.rateUsd}/GBUX
            </p>
            <p className="text-zinc-500">
              REST: GET /api/blackjack/state · POST deal/hit/stand/double ·
              play-session on deal/double
            </p>
          </div>
        )}

        <p className="text-[10px] text-zinc-600 mt-3 text-center">
          Real play GBUX vs agent bank · 3-deck CSPRNG ·{" "}
          <button
            type="button"
            className="text-emerald-500/80 underline"
            onClick={() => setLocation("/gbuxgames")}
          >
            GBUX Games
          </button>
        </p>
      </main>
    </div>
  );
}
